import * as React from 'react';

import { rgba } from 'polished';
import styled from 'styled-components';

import InputField from '@components/form/input';
import ButtonLoadingCtrl from '@webapp/controllers/button-loading';
import LinkIcon from '@components/basics/icons/link';

import { SnackbarContainer } from '@components/basics/snackbar/snackbar-container';
import SnackbarStatus from '@components/basics/snackbar/snackbar-status';
import { Translate } from 'react-localize-redux';

interface CustomUrlEditorProps {
  className?: string;
  fixed: string;
  value: string;
  fullValue: string;
  enableEditing: boolean;
  isLoading: boolean;
  isMobile: boolean;

  handleEdit: (value: string) => void;
  handleChange: (value: string) => void;
  formatter?: (value: string) => string;
  error?: React.ReactNode;
  disabled?: boolean;
  beginning?: boolean;
}

let canvas: HTMLCanvasElement;
const getTextWidth = (text: string, isMobile: boolean) => {
  if (!canvas) {
    canvas = document.createElement('canvas');
  }
  const left = 0;

  const context = canvas.getContext('2d');
  if (context) {
    if (isMobile) {
      context.font = '700 16px Inter';
    } else {
      context.font = '700 14px Inter';
    }
    const metrics = context.measureText(text);
    return left + metrics.width;
  }

  return left;
};

export const CustomUrlEditor: React.FC<CustomUrlEditorProps> = (props) => {
  const propsValue = props.value;

  const [value, setValue] = React.useState(propsValue);

  const [showNotification, setShowNotification] = React.useState(false);

  React.useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  React.useEffect(() => {
    setValue(propsValue || '');
  }, [propsValue]);

  const handleCopy = async () => {
    if (navigator?.clipboard) {
      await navigator.clipboard.writeText(props.fullValue);
      setShowNotification(true);
    }
  };

  const handleEdit = (newValue: string) => {
    if (props.formatter) {
      newValue = props.formatter(newValue);
    }
    setValue(newValue);
    props.handleEdit(newValue);
  };

  const handleSend = () => {
    props.handleChange(value);
  };

  return (
    <div className={props.className}>
      <div className="suffix-container">
        <div
          className="domain-suffix"
          style={
            props.beginning ? {} : { left: getTextWidth(value, props.isMobile) }
          }
        >
          {props.fixed}
        </div>
        <InputField
          name="url"
          style={
            props.beginning
              ? { paddingLeft: getTextWidth(props.fixed, props.isMobile) }
              : {}
          }
          className="input-field"
          label={
            props.error ? '' : <Translate id="ARTIST.CREATE.TRACPAGE.DOMAIN" />
          }
          autoComplete="off"
          value={value}
          locked={!props.enableEditing || props.disabled}
          onChange={(newValue) => handleEdit(newValue)}
          error={props.error}
          propagateClick
          rightDecoration={<LinkIcon onClick={() => handleCopy()} />}
        />
      </div>
      {showNotification ? (
        <SnackbarContainer>
          <SnackbarStatus
            status="success"
            title={<Translate id="ARTIST_SITE.COPIED" />}
            onClose={() => setShowNotification(false)}
          />
        </SnackbarContainer>
      ) : null}
      {propsValue !== value ? (
        <div className="btn-container mt-10">
          <ButtonLoadingCtrl
            isLoading={props.isLoading}
            small
            smallest
            className="inline-button-change"
            disabled={props.disabled || !!props.error || props.isLoading}
            onClick={() => handleSend()}
          >
            {props.isLoading ? null : <Translate id="URL.EDITOR.CHANGE" />}
          </ButtonLoadingCtrl>
        </div>
      ) : null}
    </div>
  );
};

export default styled(CustomUrlEditor)`
  // cursor: copy;

  .btn-container {
    overflow: hidden;
  }

  .inline-button-change {
    width: auto;
    padding: ${(props) => (props.isLoading ? '8px 32px 8px 14px' : '8px 14px')};
    min-height: 32px !important;
    min-width: 68px;
    float: right;
  }

  .inline-button {
    width: auto;
    padding: 8px 14px;
    min-height: 32px !important;
    min-width: 68px;
  }

  .suffix-container {
    position: relative;
    overflow: hidden;

    .domain-suffix {
      position: absolute;
      top: 0;

      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;

      z-index: 1;
      pointer-events: none;
      font-size: 16px;
      font-family: Inter, sans-serif;
      font-weight: 700;

      color: ${({ theme }) => rgba(theme.colors.primary, 0.6)};

      padding: 21px 0 11px 12px;

      @media ${({ theme }) => theme.device.tablet} {
        font-size: 14px;
        padding: 30px 0 14px 18px;
      }
    }
  }

  .input-field {
    input {
      z-index: 0;
    }

    .decoration-right {
      z-index: 1;
      right: auto;
      left: 85%;
      @media ${({ theme }) => theme.device.tablet} {
        left: auto;
        right: 18px;
      }
    }
  }
`;
